"use client";

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { usePullToClose } from '@/hooks/usePullToClose';
import { useClickOutsideHandler } from '@/hooks/useClickOutsideHandler';
import { MapPin, X, Zap } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useSidebarWidth } from '@/hooks/useSidebarWidth';
import useHeaderOffset from '@/hooks/useHeaderOffset';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { ToursCardList, type Tour } from './tours/ToursCardList';
import { TourInfoSidebar } from './tours/TourInfoSidebar';
import { TourCitySelector, type TourCity } from './tours/TourCitySelector';
import { useScrollLock } from '@/hooks/useScrollLock';
import { useSession } from '@/context/SessionContext';
import { useCheckout } from '@/context/CheckoutContext';
import { useToast } from '@/components/ui/use-toast';
import { addCartItem, getOpenCartsForUser, removeCartItem, updateCartItem } from '@/lib/supabase/carts';
import { addToTempCart, removeFromTempCart, updateTempCartItemQuantity } from '@/lib/tempCart';
import { DEFAULT_LOCATIONS, findCityLandmarkByDisplayName, findNearestCityLandmarkByCoords } from '@/lib/defaultLocations';

interface ActivitiesSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ActivitiesSidebar: React.FC<ActivitiesSidebarProps> = ({
  isOpen,
  onClose,
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const [selectedCity, setSelectedCity] = useState<TourCity | null>(null);
  const [selectedTours, setSelectedTours] = useState<Tour[]>([]);
  const [selectedTourQuantities, setSelectedTourQuantities] = useState<Record<string, number>>({});
  const [cartItemIdsByProductId, setCartItemIdsByProductId] = useState<Record<string, string>>({});
  const [showTourInfo, setShowTourInfo] = useState<Tour | null>(null);
  const [toursFallbackMessage, setToursFallbackMessage] = useState<string | null>(null);

  const { session, relevantLocation, profile } = useSession();
  const { openSidebar: openCheckoutSidebar } = useCheckout();
  const { toast } = useToast();
  const { maxWidth } = useSidebarWidth();
  const { isDesktop } = useHeaderOffset();
  const sidebarRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  useScrollLock(isOpen || isClosing, false, scrollAreaRef);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
      setSelectedCity(null);
      setSelectedTours([]);
      setSelectedTourQuantities({});
      setCartItemIdsByProductId({});
      setToursFallbackMessage(null);
    }, 300);
  }, [onClose]);

  usePullToClose(scrollAreaRef as React.RefObject<HTMLElement>, {
    onClose: handleClose,
    threshold: 80,
    enabled: isOpen && !isDesktop && !showTourInfo,
  });

  const handleClickOutside = useCallback(() => {
    if (!showTourInfo) {
      handleClose();
    }
  }, [showTourInfo, handleClose]);

  useClickOutsideHandler({
    isOpen,
    sidebarRef,
    handleClose: handleClickOutside,
  });

  // Handle ESC key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        event.preventDefault();
        if (showTourInfo) {
          setShowTourInfo(null);
        } else {
          handleClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleClose, showTourInfo]);

  const findCartItemIdByProductId = useCallback(async (productId: string) => {
    if (!session?.user?.id) return null;
    const carts = await getOpenCartsForUser(session.user.id);
    for (const cart of carts || []) {
      const item = (cart.cart_items || []).find((ci: any) => ci.product_id === productId);
      if (item?.id) return item.id as string;
    }
    return null;
  }, [session?.user?.id]);

  const handleTourQuantityChange = useCallback(async (tour: Tour, nextQuantity: number) => {
    const safeNextQuantity = Math.max(0, nextQuantity);
    const currentQuantity = selectedTourQuantities[tour.id] || 0;

    if (safeNextQuantity === currentQuantity) return;

    try {
      if (safeNextQuantity <= 0) {
        if (session?.user?.id) {
          const mappedId = cartItemIdsByProductId[tour.id] || await findCartItemIdByProductId(tour.id);
          if (mappedId) {
            await removeCartItem(mappedId);
            setCartItemIdsByProductId((prev) => {
              const next = { ...prev };
              delete next[tour.id];
              return next;
            });
          }
        } else {
          removeFromTempCart(tour.id);
        }

        setSelectedTours((prev) => prev.filter((t) => t.id !== tour.id));
        setSelectedTourQuantities((prev) => {
          const next = { ...prev };
          delete next[tour.id];
          return next;
        });
      } else {
        if (session?.user?.id) {
          const mappedId = cartItemIdsByProductId[tour.id] || await findCartItemIdByProductId(tour.id);
          if (mappedId) {
            await updateCartItem(mappedId, safeNextQuantity);
          } else {
            const inserted = await addCartItem(session.user.id, tour.id, safeNextQuantity);
            if (inserted?.id) {
              setCartItemIdsByProductId((prev) => ({ ...prev, [tour.id]: inserted.id }));
            }
          }
        } else {
          if (currentQuantity <= 0) {
            addToTempCart({ id: tour.id, title: tour.title, fixed_sale_price: tour.fixed_sale_price }, safeNextQuantity);
          } else {
            updateTempCartItemQuantity(tour.id, safeNextQuantity);
          }
        }

        setSelectedTours((prev) => (prev.some((t) => t.id === tour.id) ? prev : [...prev, tour]));
        setSelectedTourQuantities((prev) => ({ ...prev, [tour.id]: safeNextQuantity }));
      }

      window.dispatchEvent(new CustomEvent('cart-updated'));
    } catch (error) {
      console.error('[ActivitiesSidebar] Error syncing selected tours with cart:', error);
      toast({
        title: 'Error',
        description: 'No pudimos actualizar la excursión en tu canasto',
        variant: 'destructive',
      });
    }
  }, [cartItemIdsByProductId, findCartItemIdByProductId, selectedTourQuantities, session?.user?.id, toast]);

  const handleClearSelectedTours = useCallback(async () => {
    if (selectedTours.length === 0) return;

    try {
      if (session?.user?.id) {
        for (const tour of selectedTours) {
          const mappedId = cartItemIdsByProductId[tour.id] || await findCartItemIdByProductId(tour.id);
          if (mappedId) {
            await removeCartItem(mappedId);
          }
        }
      } else {
        selectedTours.forEach((tour) => removeFromTempCart(tour.id));
      }

      setSelectedTours([]);
      setSelectedTourQuantities({});
      setCartItemIdsByProductId({});
      window.dispatchEvent(new CustomEvent('cart-updated'));
    } catch (error) {
      console.error('[ActivitiesSidebar] Error clearing selected tours:', error);
      toast({
        title: 'Error',
        description: 'No pudimos limpiar las excursiones del canasto',
        variant: 'destructive',
      });
    }
  }, [cartItemIdsByProductId, findCartItemIdByProductId, selectedTours, session?.user?.id, toast]);

  const handleOpenToursCheckout = useCallback(() => {
    handleClose();
    setTimeout(() => {
      openCheckoutSidebar();
    }, 320);
  }, [handleClose, openCheckoutSidebar]);

  const sidebarHeight = '100dvh';

  const isUndefinedCityLabel = (value?: string | null) => {
    const normalized = (value || '').trim().toLowerCase();
    return !normalized || normalized === 'ciudad no definida' || normalized === 'ciudad desconocida';
  };

  const effectiveLocationRaw =
    relevantLocation ||
    (profile?.relevant_location_display_name &&
    profile?.relevant_latitude != null &&
    profile?.relevant_longitude != null
      ? {
          displayName: profile.relevant_location_display_name,
          latitude: profile.relevant_latitude,
          longitude: profile.relevant_longitude,
        }
      : null);

  const nearestCity = effectiveLocationRaw
    ? findNearestCityLandmarkByCoords(effectiveLocationRaw.latitude, effectiveLocationRaw.longitude)
    : null;

  const matchedByName = effectiveLocationRaw?.displayName && !isUndefinedCityLabel(effectiveLocationRaw.displayName)
    ? findCityLandmarkByDisplayName(effectiveLocationRaw.displayName)
    : null;

  const currentCityName = (
    nearestCity?.city ||
    matchedByName?.city ||
    (!isUndefinedCityLabel(effectiveLocationRaw?.displayName)
      ? effectiveLocationRaw?.displayName?.split(',')[0]?.trim()
      : null) ||
    'Salta'
  );

  const effectiveLocation = {
    displayName: currentCityName,
    latitude: effectiveLocationRaw?.latitude ?? nearestCity?.latitude ?? DEFAULT_LOCATIONS.SALTA.latitude,
    longitude: effectiveLocationRaw?.longitude ?? nearestCity?.longitude ?? DEFAULT_LOCATIONS.SALTA.longitude,
  };

  useEffect(() => {
    if (!isOpen || selectedCity) return;

    const name = (currentCityName || 'Salta').trim();
    setSelectedCity({
      city_name: name,
      region: 'Minibid',
      country: 'AR',
      latitude: effectiveLocation.latitude,
      longitude: effectiveLocation.longitude,
      tour_count: 0,
      display_name: name,
    });
  }, [isOpen, selectedCity, currentCityName, effectiveLocation.latitude, effectiveLocation.longitude]);

  const activityLocation = selectedCity
    ? { latitude: selectedCity.latitude, longitude: selectedCity.longitude }
    : { latitude: effectiveLocation.latitude, longitude: effectiveLocation.longitude };

  return (
    <AnimatePresence>
      {(isOpen) && (
        <>
          {/* Overlay - igual que CheckoutSidebar */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-transparent z-[10000]"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) {
                e.stopPropagation();
                handleClickOutside();
              }
            }}
            onTouchStart={(e) => {
              if (e.target === e.currentTarget) {
                e.stopPropagation();
                handleClickOutside();
              }
            }}
          />

          {/* Sidebar - desliza desde la derecha, mismo diseño que CheckoutSidebar */}
          <motion.div
            ref={sidebarRef}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.2 }}
            className={cn(
              'fixed right-0 w-full max-w-[100vw] lg:w-[400px] bg-background shadow-2xl z-[10001] flex flex-col overflow-x-hidden'
            )}
            style={{
              top: 0,
              bottom: !isDesktop ? 0 : undefined,
              height: isDesktop ? '100vh' : '100dvh',
              pointerEvents: isOpen ? 'auto' : 'none'
            }}
            onClick={(e) => e.stopPropagation()}
          >
          {!showTourInfo && !isDesktop && <div className="h-[env(safe-area-inset-top,0px)] bg-primary w-full shrink-0" />}

        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b shrink-0 bg-primary text-primary-foreground min-h-[64px]">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-white" />
            <h2 className="font-semibold text-lg text-white">Actividades</h2>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
            }}
            className="p-1 hover:bg-white/10 rounded-lg transition-colors text-white hover:text-white"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Content */}
        {showTourInfo ? (
          <TourInfoSidebar
            tour={showTourInfo}
            onBack={() => setShowTourInfo(null)}
          />
        ) : (
          <>
          <ScrollArea
            ref={scrollAreaRef}
            className="flex-1 overflow-x-hidden"
          >
            <div className="p-4 space-y-4">
              <div className="rounded-2xl border border-border/70 bg-background/80 p-3 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground block">Ciudad</label>
                  <span
                    className={cn(
                      "ml-2 px-2 py-0.5 rounded-full text-xs font-semibold border whitespace-nowrap transition-opacity",
                      selectedCity?.city_name === currentCityName
                        ? "bg-primary/10 text-primary border-primary/20 opacity-100"
                        : "bg-transparent text-transparent border-transparent opacity-0 pointer-events-none"
                    )}
                    aria-hidden={selectedCity?.city_name === currentCityName ? undefined : true}
                  >
                    Tu ciudad actual en Minibid
                  </span>
                </div>
                <TourCitySelector
                  selectedCity={selectedCity}
                  onCitySelect={setSelectedCity}
                  variant="minibid"
                />
              </div>

              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-semibold mb-2 block">Excursiones Disponibles</Label>
                  {!toursFallbackMessage && (
                    <p className="text-xs text-muted-foreground mb-3">
                      Mostrando actividades para {selectedCity?.city_name || currentCityName}.
                    </p>
                  )}
                  <ToursCardList
                    userLocation={activityLocation}
                    radius={50}
                    selectedCityName={selectedCity?.city_name || currentCityName}
                    selectedTourQuantities={selectedTourQuantities}
                    selectedTourIds={selectedTours.map((t) => t.id)}
                    onChangeTourQuantity={handleTourQuantityChange}
                    onViewTourInfo={(tour) => setShowTourInfo(tour)}
                    onFallbackMessageChange={setToursFallbackMessage}
                  />
                </div>
              </div>
            </div>

            {/* Footer spacer for mobile to preserve bottom breathing room */}
            {!isDesktop && <div className="h-[calc(env(safe-area-inset-bottom,0px)+80px)]" />}
          </ScrollArea>

          {selectedTours.length > 0 && (
            <div className="border-t p-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] bg-background/80 backdrop-blur-md shrink-0">
              <div className="space-y-3">
                <div className="flex justify-between items-center px-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-primary whitespace-nowrap">
                      {selectedTours.length} {selectedTours.length === 1 ? 'Excursión' : 'Excursiones'}
                    </span>
                    <div className="flex -space-x-1.5 overflow-hidden">
                      {selectedTours.map((t) => (
                        <div key={t.id} className="w-5 h-5 rounded-full border-2 border-background overflow-hidden bg-muted">
                          <img src={t.main_image_url || '/placeholder-tour.jpg'} alt={t.title} className="w-full h-full object-cover" />
                        </div>
                      ))}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); void handleClearSelectedTours(); }}
                    className="text-xs h-7 text-muted-foreground hover:text-destructive px-2"
                  >
                    Limpiar
                  </Button>
                </div>

                <Button
                  onClick={handleOpenToursCheckout}
                  className="w-full font-bold h-12 shadow-md shadow-primary/20"
                >
                  Ir al canasto con {selectedTours.length} {selectedTours.length === 1 ? 'Excursión' : 'Excursiones'}
                </Button>
              </div>
            </div>
          )}
          </>
        )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ActivitiesSidebar;
